import { useState } from "react";
import { CalendarDays, Download, Info, Loader2, MapPin, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OPS } from "@/constants/testIds";
import { DK_LONG_TIMEOUT_MS, blobErrorMessage, downloadDkExport, fmtPeriod, sanitizeQuantity } from "./dkUtils";

const C = OPS.dk;

const lockedClass =
    "flex h-10 min-w-0 items-center gap-2 rounded-[var(--radius-sm)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 text-sm font-semibold text-[color:var(--ink)]";

export const cardClass =
    "rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] shadow-[var(--shadow-sm)]";

export const chipClass = (on) =>
    `h-9 shrink-0 rounded-full border px-3 text-xs font-semibold transition-[background-color,color,border-color] duration-150 ${
        on
            ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange)] text-white shadow-[var(--shadow-sm)]"
            : "border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)]"
    }`;

/** Location picker: one location is a LOCKED chip — never a choice that does not exist. */
export function LocationPicker({ locations, value, onChange, disabled, allowAll }) {
    const { t, tn } = useI18n();
    const list = locations || [];
    const current = list.find((l) => l.id === value);
    if (list.length <= 1 && !allowAll) {
        return (
            <div data-testid={C.locationSelect} data-locked="true" className={lockedClass}>
                <MapPin className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                <span className="truncate">{current ? tn(current) : t("dk_select_location")}</span>
                {current?.is_active === false ? (
                    <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">{t("dk_inactive")}</Badge>
                ) : null}
            </div>
        );
    }
    return (
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger data-testid={C.locationSelect} className="h-10 w-full">
                <MapPin className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                <SelectValue placeholder={t("dk_select_location")} />
            </SelectTrigger>
            <SelectContent>
                {allowAll ? <SelectItem value="all" data-testid={C.locationOption("all")}>{t("dk_all_locations")}</SelectItem> : null}
                {list.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={C.locationOption(l.id)}>
                        {tn(l)}{l.is_active === false ? ` — ${t("dk_inactive")}` : ""}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/** Month select (newest first); never the native `type="month"`, which some desktops render as text. */
export function MonthPicker({ months, value, onChange, testId, disabled, className = "" }) {
    const { lang } = useI18n();
    return (
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger data-testid={testId || C.monthSelect} data-month={value} className={`h-10 w-full ${className}`}>
                <CalendarDays className="h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {(months || []).map((m) => <SelectItem key={m} value={m}>{fmtPeriod(m, lang)}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}

/** No response at all (timeout, dropped connection): the inline offline / retry box. */
export function OfflineBox({ onRetry, busy, text }) {
    const { t } = useI18n();
    return (
        <div data-testid={C.offlineError}
             className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
            <span className="flex min-w-0 items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                <WifiOff className="h-3.5 w-3.5 shrink-0" />
                {text || t("dk_offline_error")}
            </span>
            <Button size="sm" variant="outline" data-testid={C.retry} onClick={onRetry} disabled={busy}>
                {t("dk_retry")}
            </Button>
        </div>
    );
}

/** A dashed note (server message, empty state). */
export function NoteBox({ children, testId, icon: Icon = Info }) {
    return (
        <div data-testid={testId || C.note}
             className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-4 py-3">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
            <div className="min-w-0 text-sm font-semibold text-[color:var(--ink-muted)]">{children}</div>
        </div>
    );
}

/**
 * Quantity input with the unit as a suffix. RTL-safe: the WRAPPER is `dir="ltr"`,
 * so `end-3` and the input's `pe-*` are the same physical side and the Arabic unit
 * never sits over the digits. 16-px text (no iOS zoom), Arabic digits folded.
 */
export function QtyField({ value, onChange, unit, allowsDecimal, testId, invalid, onEnter, label, disabled, placeholder }) {
    const { t } = useI18n();
    return (
        <div className="relative min-w-0" dir="ltr">
            <Input
                data-testid={testId}
                data-dk-qty=""
                value={value ?? ""}
                onChange={(e) => onChange(sanitizeQuantity(e.target.value, allowsDecimal))}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); onEnter && onEnter(e.currentTarget); }
                }}
                inputMode={allowsDecimal ? "decimal" : "numeric"}
                enterKeyHint="next"
                autoComplete="off"
                aria-label={label}
                aria-invalid={invalid ? "true" : undefined}
                placeholder={placeholder ?? t("dk_qty_placeholder")}
                disabled={disabled}
                dir="ltr"
                className={`h-11 pe-16 text-base font-semibold tabular-nums ${invalid ? "border-[color:var(--danger)]" : ""}`}
            />
            {unit ? (
                <span className="pointer-events-none absolute inset-y-0 end-3 flex max-w-[3.5rem] items-center">
                    <span className="truncate text-xs font-medium text-[color:var(--ink-muted)]">{unit}</span>
                </span>
            ) : null}
        </div>
    );
}

/** «تصدير Excel» for one month (`GET /dk/export`); a refused export carries its JSON error in the blob. */
export function ExportButton({ month, testId }) {
    const { t, lang } = useI18n();
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            await downloadDkExport(month, lang, DK_LONG_TIMEOUT_MS);
            toast.success(t("dk_exported"));
        } catch (e) {
            toast.error((await blobErrorMessage(e, lang)) || t("dk_export_failed"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <button type="button" data-testid={testId} onClick={run} disabled={busy || !month}
                className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-[color:var(--brand-brown)] px-4 text-xs font-bold text-white shadow-[var(--shadow-sm)] transition-[opacity,transform] duration-150 active:scale-[0.97] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {busy ? t("dk_exporting") : t("dk_export")}
        </button>
    );
}

/** The sales-data state of a day: one short sentence, server label first. */
export function dataStateText(state, scope, lang, t) {
    if (!state || state === "ok") return "";
    const row = (scope?.labels?.states || []).find((x) => x && x.key === state);
    if (row) return lang === "ar" ? row.name_ar || row.name_en : row.name_en || row.name_ar;
    const key = { no_sales: "dk_state_no_sales", not_synced: "dk_state_not_synced", no_pos_id: "dk_state_no_pos_id" }[state];
    return key ? t(key) : state;
}
